import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, Clock, Zap, Droplet, Truck, ShieldCheck, ArrowRight, Check, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://customer-assets-v7afamib.emergentagent.net/job_quick-clean-pickup/artifacts/zcij27s6_4D8F9033-D002-4F67-9355-ED4B2256BF8B.PNG";
const SERVICE_IMG = "https://images.unsplash.com/photo-1626806819282-2c1dc01a5e0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHx3YXNoaW5nJTIwbWFjaGluZSUyMG1vZGVybnxlbnwwfHx8fDE3ODMyNTA0MzB8MA&ixlib=rb-4.1.0&q=85";
const COURIER_IMG = "https://images.unsplash.com/photo-1551825687-f9de1603ed8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxkZWxpdmVyeSUyMGNvdXJpZXIlMjBzbWlsaW5nfGVufDB8fHx8MTc4MzI1MDQyOXww&ixlib=rb-4.1.0&q=85";
const BASKET_IMG = "https://images.unsplash.com/photo-1582735689369-4fe89db7114c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMGNsZWFuJTIwbGF1bmRyeSUyMGJhc2tldHxlbnwwfHx8fDE3ODMyNTA0Mjl8MA&ixlib=rb-4.1.0&q=85";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [giftBanner, setGiftBanner] = React.useState(null);

  // Capture ?ref= code from URL and persist it for the signup / Google flow
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    const mode = (params.get("mode") || "").toLowerCase();
    if (ref) {
      try {
        localStorage.setItem("foldora_ref", ref.toUpperCase());
        localStorage.setItem("foldora_ref_mode", mode === "gift" ? "gift" : "standard");
      } catch { /* ignore */ }
      if (mode === "gift") setGiftBanner("$20");
      else setGiftBanner("$10");
    }
  }, []);

  const startBooking = () => navigate(user ? "/book" : "/signup");

  return (
    <div data-testid="landing-page" className="min-h-screen bg-stone-50">
      <Navbar transparent />

      {giftBanner && !user && (
        <div data-testid="gift-banner" className="bg-pink-500 text-white text-center py-3 px-4 font-heading font-bold text-sm sm:text-base">
          🎁 A friend gifted you <span className="font-black">{giftBanner} off</span> your first Foldora order — sign up to redeem.
        </div>
      )}

      {!giftBanner && (
        <div data-testid="free-delivery-bar" className="bg-[#4B6043] text-white text-center py-2 px-4 font-heading font-bold text-xs sm:text-sm tracking-wide">
          <span className="block sm:inline">🚚 Free pickup &amp; delivery on orders $50+ · Prices based on 30-min drive from Laval</span>
          <span className="hidden sm:inline mx-2 text-pink-200">|</span>
          <span className="block sm:inline text-pink-100">🇫🇷 Cueillette et livraison gratuites sur les commandes de 50 $+ · Tarifs basés sur un rayon de 30 min de Laval</span>
        </div>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="blob bg-pink-300 w-[400px] h-[400px] -top-24 -left-24" />
        <div className="blob bg-rose-200 w-[500px] h-[500px] -bottom-40 right-0" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-pink-100 px-4 py-1.5 text-pink-700 font-bold text-xs uppercase tracking-[0.2em] fade-up">
              <Sparkles className="w-3.5 h-3.5" /> 24-Hour Turnaround
            </div>
            <h1 data-testid="hero-title" className="font-heading text-5xl lg:text-7xl font-black tracking-tighter text-stone-900 mt-6 leading-[0.95] fade-up delay-1">
              Fresh laundry.<br />
              <span className="text-pink-500">Picked up.</span><br />
              Delivered <span className="italic font-serif">tomorrow.</span>
            </h1>
            <p className="mt-6 text-lg text-stone-600 max-w-lg fade-up delay-2">
              Book a pickup, choose your detergent, we handle the rest. Pickups Mon / Wed / Fri, drop-offs Tue / Thu / Sat — or add express for same-day service.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4 fade-up delay-3">
              <Button
                data-testid="hero-cta-primary"
                onClick={startBooking}
                className="rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold text-base h-14 px-8 shadow-lg shadow-pink-500/30 hover:-translate-y-0.5 transition-all"
              >
                Schedule a Pickup <ArrowRight className="w-5 h-5 ml-1" />
              </Button>
              {!user && (
                <Button
                  data-testid="hero-google-signin"
                  onClick={loginWithGoogle}
                  variant="outline"
                  className="rounded-full h-14 px-6 border-2 border-stone-300 bg-white hover:bg-stone-50 font-bold text-base hover:-translate-y-0.5 transition-all"
                >
                  <img alt="Google" src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5 mr-2" />
                  Sign in with Google
                </Button>
              )}
              <a href="#how" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 font-bold text-stone-900 border-b-2 border-stone-900 pb-1 hover:text-pink-600 hover:border-pink-600 transition-colors">
                See how it works
              </a>
            </div>
            {!user && (
              <p className="mt-3 text-xs text-stone-500 fade-up delay-3">
                One tap · no password · book your first pickup in under 60 seconds.
              </p>
            )}
            <div className="mt-10 flex items-center gap-6 fade-up delay-4">
              <div className="flex -space-x-3">
                {[COURIER_IMG, BASKET_IMG, SERVICE_IMG].map((src, i) => (
                  <img key={i} src={src} alt="" className="w-11 h-11 rounded-full border-4 border-white object-cover" />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-pink-500">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="text-sm text-stone-600 font-semibold">Loved by 12,000+ households</p>
              </div>
            </div>
          </div>
          <div className="relative fade-up delay-2">
            <div className="absolute -inset-6 bg-pink-100 rounded-[3rem] -rotate-3" />
            <img
              src={HERO_IMG}
              alt="Foldora — we fold, you relax"
              className="relative rounded-[2.5rem] w-full h-[560px] object-contain bg-white p-8 shadow-xl"
            />
            <Card className="absolute -bottom-8 -left-8 p-5 bg-white rounded-2xl border-stone-200 shadow-xl w-[240px]">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-pink-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-stone-400">Next slot</div>
                  <div className="font-heading font-bold text-stone-900">Today · 5–7 PM</div>
                </div>
              </div>
            </Card>
            <Card className="absolute -top-6 -right-6 p-4 bg-pink-500 rounded-2xl border-0 shadow-xl">
              <div className="text-white font-heading font-black text-2xl leading-none">+$15</div>
              <div className="text-pink-100 text-xs font-bold uppercase tracking-widest">Express Delivery</div>
            </Card>
          </div>
        </div>
      </section>

      {/* LOGO STRIP */}
      <section className="border-y border-stone-200 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center gap-10">
          <p className="font-heading font-bold text-stone-400 uppercase tracking-[0.2em] text-xs shrink-0">Featured in</p>
          <div className="flex gap-10 overflow-hidden">
            {["The Fresh Times", "Urban Living", "Home Weekly", "Modern Life", "City Digest"].map((n) => (
              <span key={n} className="font-heading font-black text-stone-300 text-xl whitespace-nowrap">{n}</span>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500 mb-3">Our services</div>
          <h2 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter text-stone-900">
            Three tiers. Zero guesswork.
          </h2>
          <p className="mt-4 text-stone-600 text-lg">Choose what your load needs &mdash; we treat every bundle like it&rsquo;s our own.</p>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {[
            { name: "Wash & Fold", price: "$50 minimum · $2/lb over 25 lbs", desc: "Everyday clothes washed with your preferred detergent, dried, and neatly folded.", icon: <Droplet className="w-6 h-6" /> },
            { name: "Dry Clean", price: "By quote", desc: "Delicate garments, suits, and dresses handled by professional dry cleaners.", icon: <Sparkles className="w-6 h-6" /> },
            { name: "Ironing", price: "+$5 per item", desc: "Crisp, ready-to-wear ironing service for shirts, trousers, and linens.", icon: <Zap className="w-6 h-6" /> },
          ].map((s) => (
            <Card
              key={s.name}
              data-testid={`service-card-${s.name.toLowerCase().replace(/\W/g, "-")}`}
              className="p-8 rounded-3xl border border-stone-200 bg-white hover:-translate-y-1 hover:shadow-md transition-all"
            >
              <div className="w-12 h-12 rounded-2xl bg-pink-100 flex items-center justify-center text-pink-600 mb-6">
                {s.icon}
              </div>
              <div className="font-heading font-black text-2xl text-stone-900">{s.name}</div>
              <div className="mt-1 text-pink-500 font-bold text-lg">{s.price}</div>
              <p className="mt-4 text-stone-600 leading-relaxed">{s.desc}</p>
              <button
                onClick={s.name === "Dry Clean" ? () => { window.location.href = "mailto:info@foldora.ca?subject=Dry%20Clean%20Quote%20Request"; } : startBooking}
                data-testid={`service-book-${s.name.toLowerCase().replace(/\W/g, "-")}`}
                className="mt-6 inline-flex items-center gap-1.5 font-bold text-stone-900 border-b-2 border-stone-900 pb-0.5 hover:text-pink-600 hover:border-pink-600"
              >
                {s.name === "Dry Clean" ? "Request quote" : "Book now"} <ArrowRight className="w-4 h-4" />
              </button>
            </Card>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-pink-50 py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <img src={SERVICE_IMG} alt="Modern laundry" className="rounded-3xl w-full object-cover h-[540px] shadow-lg" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500 mb-3">How it works</div>
            <h2 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter text-stone-900">
              From dirty bag to<br /> folded stack — in <span className="text-pink-500">24h</span>.
            </h2>
            <div className="mt-10 space-y-6">
              {[
                { t: "Book a pickup", d: "Pick a Mon / Wed / Fri slot (9–11 AM) and your preferred detergent." },
                { t: "We collect", d: "A friendly Foldora courier picks up your laundry at your door." },
                { t: "Sparkle treatment", d: "Sorted, washed at the right temperature, dried, folded, and packed." },
                { t: "Delivered next slot", d: "Drop-off on Tue / Thu / Sat, 3–6 PM. Need it sooner? Add express for +$15." },
              ].map((step, i) => (
                <div key={step.t} className="flex gap-5 items-start">
                  <div className="w-11 h-11 shrink-0 rounded-full bg-pink-500 text-white font-heading font-black text-lg flex items-center justify-center">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-heading font-bold text-xl text-stone-900">{step.t}</div>
                    <p className="text-stone-600 mt-1">{step.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DETERGENT CHOICE */}
      <section id="detergents" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500 mb-3">Detergent choice</div>
          <h2 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter text-stone-900">
            Pick your perfect wash.
          </h2>
          <p className="mt-4 text-stone-600 text-lg">
            Five premium options — from fragrance-free to fresh-scented. Or bring your own, we&rsquo;ll only use what you provide.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {[
            { name: "Fresh Scent", desc: "A light, long-lasting fresh fragrance.",
              img: "https://customer-assets-v7afamib.emergentagent.net/job_quick-clean-pickup/artifacts/9vjrv2bg_CE797797-566D-49A6-A180-58F348CB1463.PNG",
              bg: "bg-pink-50" },
            { name: "Free & Clear", desc: "Free from dyes and fragrances. Ideal for sensitive skin.",
              img: "https://customer-assets-v7afamib.emergentagent.net/job_quick-clean-pickup/artifacts/084f9anh_E538D87B-1A71-4412-8661-DD39C9C3BBE2.PNG",
              bg: "bg-sky-50" },
            { name: "Hypoallergenic", desc: "Gentle on skin, perfect for sensitive skin and babies.",
              img: "https://customer-assets-v7afamib.emergentagent.net/job_quick-clean-pickup/artifacts/8ee6d20r_F5B44677-D117-4BC1-B4C7-A54E28520D8A.PNG",
              bg: "bg-emerald-50" },
            { name: "Powder Detergent", desc: "Deep-clean formula, tough on stains and gentle on clothes.",
              img: "https://customer-assets-v7afamib.emergentagent.net/job_quick-clean-pickup/artifacts/zumwiyjp_FD0600B3-CD18-48D9-8B6B-45AA62EACEAD.PNG",
              bg: "bg-pink-100" },
            { name: "Laundry Pods", desc: "Pre-measured for convenience, powerful and easy to use.",
              img: "https://customer-assets-v7afamib.emergentagent.net/job_quick-clean-pickup/artifacts/txgglvhi_E7603ADB-DE27-4847-A337-CB092AC1E3CC.PNG",
              bg: "bg-indigo-50" },
          ].map((d) => (
            <div
              key={d.name}
              data-testid={`detergent-card-${d.name.toLowerCase().replace(/\W+/g, "-")}`}
              className="rounded-3xl bg-white border border-stone-200 p-4 hover:-translate-y-1 hover:shadow-md transition-all flex flex-col"
            >
              <div className={`${d.bg} rounded-2xl p-4 h-44 flex items-center justify-center`}>
                <img src={d.img} alt={d.name} className="max-h-full max-w-full object-contain" />
              </div>
              <div className="mt-4 font-heading font-black text-lg text-stone-900">{d.name}</div>
              <p className="text-sm text-stone-600 mt-1 leading-relaxed">{d.desc}</p>
            </div>
          ))}
        </div>

        {/* WHY CUSTOMERS LOVE + IMPORTANT NOTE */}
        <div className="mt-16 grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-8 rounded-3xl border border-stone-200 bg-white">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500 mb-2">Why customers love having a choice</div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {[
                { icon: <Sparkles className="w-4 h-4" />, t: "Personalized for your preferences" },
                { icon: <Droplet className="w-4 h-4" />, t: "Gentle options available" },
                { icon: <ShieldCheck className="w-4 h-4" />, t: "Safe for sensitive skin" },
                { icon: <Check className="w-4 h-4" />, t: "Fresh, clean results every time" },
                { icon: <Star className="w-4 h-4" />, t: "Your detergent choice is saved for future orders" },
              ].map((p) => (
                <div key={p.t} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-pink-100 text-pink-600 flex items-center justify-center shrink-0">{p.icon}</div>
                  <div className="text-stone-800 font-semibold pt-1">{p.t}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-8 rounded-3xl border-2 border-pink-500 bg-pink-50">
            <div className="text-xs font-black uppercase tracking-[0.2em] text-pink-700 mb-3">Important note</div>
            <p className="text-stone-800 leading-relaxed">
              All detergents are high quality and chosen for their cleaning power and care.
            </p>
            <div className="mt-4 pt-4 border-t border-pink-200">
              <p className="font-heading font-bold text-stone-900">Not sure which is right for you?</p>
              <p className="text-sm text-stone-700 mt-2">
                Choose <span className="font-bold text-pink-700">Hypoallergenic</span> or <span className="font-bold text-pink-700">Free &amp; Clear</span> if you have sensitive skin or fragrance allergies.
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500 mb-3">Simple pricing</div>
            <h2 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter text-stone-900">
              Flat rates. No surprises.
            </h2>
          </div>
          <p className="max-w-md text-stone-600">Choose standard 24-hour delivery included with every order — or express same-day for a flat +$15.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-10 rounded-3xl border border-stone-200 bg-white">
            <div className="flex items-baseline gap-2">
              <div className="font-heading font-black text-5xl text-stone-900">Standard</div>
            </div>
            <div className="mt-2 text-pink-500 font-bold">$2 / lb · $50 minimum order</div>
            <ul className="mt-8 space-y-3 text-stone-700">
              {[
                "Pickup Mon / Wed / Fri · 9 – 11 AM",
                "Drop-off Tue / Thu / Sat · 3 – 6 PM",
                "Choice of 5 detergents",
                "SMS updates",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-pink-500" /> {t}</li>
              ))}
            </ul>
          </Card>
          <Card className="p-10 rounded-3xl border-2 border-pink-500 bg-[#4B6043] text-white relative overflow-hidden">
            <div className="absolute top-6 right-6 bg-pink-500 rounded-full px-3 py-1 text-xs font-black uppercase tracking-widest">Popular</div>
            <div className="font-heading font-black text-5xl">Express</div>
            <div className="mt-2 text-pink-300 font-bold">+$15 · same-day delivery</div>
            <ul className="mt-8 space-y-3 text-stone-200">
              {["Same-day return", "Priority handling", "All standard perks", "Priority support"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-pink-400" /> {t}</li>
              ))}
            </ul>
            <Button
              data-testid="pricing-express-cta"
              onClick={startBooking}
              className="mt-8 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold h-12 px-6"
            >
              Book Express <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Card>
        </div>
      </section>

      {/* PERKS */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { icon: <Truck className="w-5 h-5" />, t: "Free pickup + delivery", d: "On every $50+ order." },
            { icon: <ShieldCheck className="w-5 h-5" />, t: "Damage protection", d: "Fully insured." },
            { icon: <Droplet className="w-5 h-5" />, t: "Detergent choice", d: "5 options." },
            { icon: <Zap className="w-5 h-5" />, t: "Fast turnaround", d: "24h standard." },
          ].map((p) => (
            <div key={p.t} className="rounded-3xl p-6 bg-white border border-stone-200">
              <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center mb-4">{p.icon}</div>
              <div className="font-heading font-bold text-lg text-stone-900">{p.t}</div>
              <div className="text-stone-600 text-sm">{p.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MEMBERSHIP */}
      <section id="membership" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500 mb-3">Monthly membership</div>
            <h2 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter text-stone-900">
              Never fold laundry again.
            </h2>
            <p className="mt-3 text-stone-600 max-w-md">Recurring plans for households, families, and Airbnb hosts. Cancel anytime.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {/* Essential */}
          <Card data-testid="plan-essential" className="p-8 rounded-3xl border border-stone-200 bg-white hover:-translate-y-1 transition-all">
            <div className="text-xs font-bold uppercase tracking-widest text-stone-400">Essential</div>
            <div className="mt-3 font-heading text-5xl font-black tracking-tighter text-stone-900">$160<span className="text-lg font-bold text-stone-500">/mo</span></div>
            <ul className="mt-6 space-y-3 text-stone-700">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-500 mt-1 shrink-0" /> 4 pickups per month</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-500 mt-1 shrink-0" /> Up to 30 lb per pickup</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-500 mt-1 shrink-0" /> Scheduled Mon / Wed / Fri</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-500 mt-1 shrink-0" /> 5 detergent options</li>
            </ul>
            <Button data-testid="plan-essential-cta" onClick={startBooking} variant="outline" className="mt-8 w-full rounded-full h-12 border-stone-300 font-bold">Choose Essential</Button>
          </Card>

          {/* Family — highlighted */}
          <Card data-testid="plan-family" className="p-8 rounded-3xl border-2 border-pink-500 bg-[#4B6043] text-white relative overflow-hidden">
            <div className="absolute top-6 right-6 bg-pink-500 rounded-full px-3 py-1 text-xs font-black uppercase tracking-widest">Most popular</div>
            <div className="text-xs font-bold uppercase tracking-widest text-pink-200">Family</div>
            <div className="mt-3 font-heading text-5xl font-black tracking-tighter">$320<span className="text-lg font-bold text-pink-200">/mo</span></div>
            <ul className="mt-6 space-y-3 text-stone-100">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-300 mt-1 shrink-0" /> 8 pickups per month</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-300 mt-1 shrink-0" /> Up to 30 lb per pickup</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-300 mt-1 shrink-0" /> Priority scheduling</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-300 mt-1 shrink-0" /> Free rush service once per month</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-300 mt-1 shrink-0" /> All Essential perks</li>
            </ul>
            <Button data-testid="plan-family-cta" onClick={startBooking} className="mt-8 w-full rounded-full h-12 bg-pink-500 hover:bg-pink-600 text-white font-bold">Choose Family</Button>
          </Card>

          {/* Premium — custom quote */}
          <Card data-testid="plan-premium" className="p-8 rounded-3xl border border-stone-200 bg-white hover:-translate-y-1 transition-all">
            <div className="text-xs font-bold uppercase tracking-widest text-stone-400">Premium</div>
            <div className="mt-3 font-heading text-3xl font-black tracking-tighter text-stone-900">Custom quote<span className="block text-sm font-semibold text-stone-500 mt-1">Based on your needs</span></div>
            <ul className="mt-6 space-y-3 text-stone-700">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-500 mt-1 shrink-0" /> Unlimited scheduled pickups</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-500 mt-1 shrink-0" /> Ideal for Airbnb hosts &amp; small businesses</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-500 mt-1 shrink-0" /> Dedicated account manager</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-pink-500 mt-1 shrink-0" /> Includes all Family perks</li>
            </ul>
            <a href="mailto:info@foldora.ca?subject=Premium%20Foldora%20Plan%20Inquiry" data-testid="plan-premium-cta" className="mt-8 inline-flex items-center justify-center w-full rounded-full h-12 bg-stone-900 hover:bg-stone-800 text-white font-bold">
              Request a quote
            </a>
          </Card>
        </div>
        <p className="mt-6 text-center text-xs text-stone-500">All membership prices are based on 30 minute driving time from our Laval hub.</p>
      </section>

      {/* SPECIALTY / BY QUOTE ONLY */}
      <section id="specialty" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid lg:grid-cols-3 gap-6 items-stretch">
          <div className="lg:col-span-1">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500 mb-3">By quote only</div>
            <h2 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter text-stone-900 leading-[0.95]">
              Specialty care.<br /><span className="text-pink-500">Custom quotes.</span>
            </h2>
            <p className="mt-4 text-stone-600 max-w-sm">
              For items that deserve extra attention, request a personalised quote. We&rsquo;ll reply within one business day.
            </p>
            <a
              href="mailto:info@foldora.ca?subject=Specialty%20Service%20Quote%20Request"
              data-testid="specialty-quote-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 h-12"
            >
              Contact Us <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-3 gap-4">
            {[
              { name: "Dry Cleaning", desc: "Suits, dresses, delicates &amp; wool.", icon: <Sparkles className="w-5 h-5" /> },
              { name: "Bridal", desc: "Wedding gown cleaning &amp; preservation.", icon: <ShieldCheck className="w-5 h-5" /> },
              { name: "Dress Alteration", desc: "Hemming, resizing, minor tailoring.", icon: <Zap className="w-5 h-5" /> },
            ].map((s) => (
              <div
                key={s.name}
                data-testid={`specialty-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-3xl p-6 bg-white border border-stone-200 flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center mb-4">{s.icon}</div>
                  <div className="font-heading font-black text-xl text-stone-900">{s.name}</div>
                  <p className="text-sm text-stone-600 mt-2" dangerouslySetInnerHTML={{__html: s.desc}} />
                </div>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-pink-600">By quote only</span>
                  <a
                    href={`mailto:info@foldora.ca?subject=${encodeURIComponent(s.name + " — Quote Request")}`}
                    data-testid={`specialty-contact-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="inline-flex items-center gap-1 text-xs font-bold text-stone-900 border-b-2 border-stone-900 pb-0.5 hover:text-pink-600 hover:border-pink-600 transition-colors"
                  >
                    Contact Us <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT FOLDORA */}
      <section id="about" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden rounded-[3rem] bg-white border border-stone-200 p-10 lg:p-16">
          <div className="absolute -top-16 -right-16 w-60 h-60 rounded-full bg-pink-100 opacity-70" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-pink-50 opacity-80" />
          <div className="relative grid lg:grid-cols-3 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-1">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500 mb-3">About Foldora</div>
              <h2 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter text-stone-900 leading-[0.95]">
                Made in <span className="text-pink-500">Laval</span>. <br />Loved across Montreal.
              </h2>
            </div>
            <div className="lg:col-span-2 space-y-5 text-stone-700 text-lg leading-relaxed">
              <p>
                <span className="font-heading font-black text-stone-900">Foldora</span> is a laundry pickup and delivery
                service based in <b>Laval, Quebec</b>, serving customers throughout Laval and the Montreal area. We provide
                convenient wash, dry, and fold services, along with optional ironing and linen care, to help busy individuals,
                families, students, seniors, and Airbnb hosts save time.
              </p>
              <p>
                Our goal is to deliver reliable, affordable, and high-quality laundry services with excellent customer care.
                Foldora will grow through online bookings, recurring customers, local partnerships, and digital marketing. As
                the business expands, we plan to increase our service area, hire additional staff, and become a trusted name in
                laundry pickup and delivery across the Greater Montreal region.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden rounded-[3rem] bg-[#4B6043] text-white p-12 lg:p-20">
          <img src={BASKET_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="relative z-10 max-w-3xl">
            <h3 className="font-heading text-4xl lg:text-6xl font-black tracking-tighter">
              Skip the laundromat.<br /><span className="text-pink-400">Get your Sunday back.</span>
            </h3>
            <p className="mt-6 text-stone-200 text-lg max-w-xl">
              Join thousands who never fold again. Book your first pickup in under 60 seconds.
            </p>
            <Button
              data-testid="footer-cta"
              onClick={startBooking}
              className="mt-8 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold text-base h-14 px-8"
            >
              Schedule my first pickup <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="font-heading font-black text-xl text-stone-900">Foldora</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center text-sm font-semibold text-stone-600">
            <a href="/faq" data-testid="footer-faq" className="hover:text-pink-600">FAQ</a>
            <a href="/contact" data-testid="footer-contact" className="hover:text-pink-600">Contact</a>
            <a href="/legal/privacy" data-testid="footer-privacy" className="hover:text-pink-600">Privacy</a>
            <a href="/legal/terms" data-testid="footer-terms" className="hover:text-pink-600">Terms</a>
            <a href="/legal/refund" data-testid="footer-refund" className="hover:text-pink-600">Refund</a>
            <a href="/legal/service-agreement" data-testid="footer-agreement" className="hover:text-pink-600">Service Agreement</a>
            <a href="/legal/damage" data-testid="footer-damage" className="hover:text-pink-600">Damage Policy</a>
          </div>
          <div className="text-sm text-stone-500">© {new Date().getFullYear()} Foldora</div>
        </div>
      </footer>
    </div>
  );
}
